import { Flex, Select, Switch, Card, Checkbox } from "antd";
import { useEditProductContext } from "../../../../contexts/edit-product-context/useEditProductContext";
import styled from "styled-components";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-inner {
    width: 18px !important;
    height: 18px !important;
  }

  .ant-checkbox-checked .ant-checkbox-inner::after {
    width: 6px !important;
    height: 10px !important;
  }
`;

export const ProductBaseVariant: React.FC<any> = ({
  productBaseVariant,
  category,
  options,
  hasMultipleProductVariants,
  hasError = false,
}) => {
  const { t } = useTranslation();
  const {
    extensionsConfig,
    setExtensionsConfig,
    setUnsavedChanges,
    productBaseVariants,
  } = useEditProductContext();

  // Pobierz IDs dodatków z tej kategorii
  const categoryExtensionIds = useMemo(
    () => category?.extensions?.map((e: any) => e.id) ?? [],
    [category]
  );

  // Oblicz aktualną wartość z extensionsConfig dla tej kategorii
  const currentValue = useMemo(() => {
    if (options.length === 1) {
      return options[0].value;
    }

    // Szukaj wartości tylko wśród dodatków z tej kategorii
    const variantConfig = extensionsConfig?.[productBaseVariant.key] ?? {};
    for (const extId of categoryExtensionIds) {
      const config = variantConfig[extId];
      if (config?.name) {
        // Sprawdź czy ta wartość jest w dostępnych opcjach
        const matchingOption = options.find((opt: any) => opt.value === config.name);
        if (matchingOption) {
          return config.name;
        }
      }
    }
    return undefined;
  }, [options, extensionsConfig, productBaseVariant.key, categoryExtensionIds]);

  const [value, setValue] = useState(currentValue);

  // Synchronizuj value z extensionsConfig gdy się zmieni (np. po załadowaniu danych)
  useEffect(() => {
    setValue(currentValue);
  }, [currentValue]);
  const onChangeHandler = (value: any) => {
    const ids = category?.extensions?.map((e: any) => e.id) ?? [];
    let config: any = extensionsConfig;
    const config2: any = {};
    Object.keys(config?.[productBaseVariant.key]).forEach((key) => {
      if (ids.includes(Number(key))) {
        config2[Number(key)] = { name: value };
      }
    });

    config = {
      ...config,
      [productBaseVariant.key]: {
        ...config?.[productBaseVariant.key],
        ...config2,
      },
    };
    setValue(value);
    setExtensionsConfig({ ...config });
    setUnsavedChanges(true);
  };
  const shouldShowVariantSection = hasMultipleProductVariants || options.length > 1;

  return (
    <>
      {shouldShowVariantSection && (
        <Flex
          justify={options.length > 1 && !hasMultipleProductVariants ? "flex-end" : "space-between"}
          align="center"
          style={{
            background: "var(--color-background-secondary)",
            padding: "12px 16px",
          }}
        >
          {/* Pokazuj tekst tylko gdy:
              - grupa ma 1 wariant (opcja "Ceny dodatków dla: [nazwa]")
              - LUB produkt ma wiele wariantów (opcja "Cena dla wariantu: [nazwa]") */}
          {(options.length === 1 || hasMultipleProductVariants) && (
            <span>
              {options.length === 1 ? (
                <>{t("productBaseVariant.pricesForLabel")} <b>{productBaseVariant.name}</b></>
              ) : (
                <>{t("productBaseVariant.priceForVariantLabel")} <b>{productBaseVariant.name}</b></>
              )}
            </span>
          )}
          {options.length > 1 && (
            <Flex vertical justify="start" align="end" gap={4}>
              <span style={{ fontSize: "12px", color: hasError ? "var(--color-error)" : "var(--color-text-secondary)" }}>
                {hasError ? t("productBaseVariant.selectPriceVariant") : t("productBaseVariant.priceVariant")}
              </span>
              <Select
                style={{
                  width: "200px",
                }}
                status={hasError && (!value && !currentValue) ? "error" : undefined}
                value={value ?? currentValue}
                options={options}
                onChange={onChangeHandler}
              />
            </Flex>
          )}
        </Flex>
      )}
      {category.extensions
        ?.sort((a: any, b: any) => a.menuPosition - b.menuPosition)
        ?.map((e: any) => {
          const price =
            e.productExtensionVariant.find(
              (e: any) => e.name === (value ?? currentValue)
            )?.priceExtension / 100;

          const label = isNaN(price) ? "-" : price.toFixed(2) + " PLN";

          // Sprawdź stan checkboxa - jeśli grupa ma tylko 1 wariant, sprawdź czy jest we wszystkich wariantach produktu
          const isChecked = options.length === 1
            ? productBaseVariants.every((variant) => !!extensionsConfig?.[variant.key]?.[e.id])
            : !!extensionsConfig?.[productBaseVariant.key]?.[e.id];

          return (
            <Flex
              justify="space-between"
              align="center"
              gap={12}
              style={{ paddingLeft: 16, paddingRight: 16, height: "45px" }}
            >
              <Flex justify="start" align="center" gap={12}>
                <StyledCheckbox
                  checked={isChecked}
                  onChange={(val) => {
                    const config: any = extensionsConfig;

                    // Jeśli grupa dodatków ma tylko 1 wariant cenowy, aktualizuj dla wszystkich wariantów produktu
                    if (options.length === 1) {
                      productBaseVariants.forEach((variant) => {
                        if (val.target.checked) {
                          config[variant.key] = {
                            ...config[variant.key],
                            [`${e.id}`]: { name: value ?? currentValue },
                          };
                        } else {
                          if (config[variant.key]) {
                            delete config[variant.key][e.id];
                          }
                        }
                      });
                    } else {
                      // Jeśli grupa ma wiele wariantów, aktualizuj tylko dla obecnego wariantu produktu
                      if (val.target.checked) {
                        config[productBaseVariant.key] = {
                          ...config[productBaseVariant.key],
                          [`${e.id}`]: { name: value ?? currentValue },
                        };
                      } else {
                        delete config[productBaseVariant.key][e.id];
                      }
                    }

                    setExtensionsConfig({ ...config });
                    setUnsavedChanges(true);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <p>{e.name} </p>
              </Flex>
              <Flex justify="start" align="center" gap={12}>
                <p>{label}</p>
              </Flex>
            </Flex>
          );
        })}
    </>
  );
};
